/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import AppText from "components/AppText";
import {
  faCheckCircle,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { Oval } from "react-loader-spinner";
import { GoogleOAuthProvider } from "@react-oauth/google";
import GoogleScopeLogin from "components/GoogleScopeLogin";
import { getGoogleAdsAccounts, getRefreshToken } from "functions/googleAds";
import MaterialTreeView from "components/MaterialTreeView";
import { sweetAlert } from "functions/global";
import "./styles.css";
import AppButton from "components/AppButton";
import LinkBridgeSuccess from "components/LinkBridgeSuccess";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { ApiServerPath } from "api_server";
import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Button from "@mui/material/Button";

const steps = ["Connect Account", "Confirm Access", "Connected"];

const GoogleLinkBridge = ({ connectId, workspaceId }: any) => {
  const [activeStep, setActiveStep] = React.useState(0);
  const [loading, setLoading] = useState(false);
  const [googleLoginLoading, setGoogleLoginLoading] = useState(false);
  const [accountSelectLoading, setAccountSelectLoading] = useState(false);
  const [accessLoading, setAccessLoading] = useState(false);
  const [adminDetails, setAdminDetails] = useState<any>(null);
  const [errorFromAdmin, setErrorFromAdmin] = useState("");
  const [googleRefreshToken, setGoogleRefreshToken] = useState<any>(null);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [selectedGoogleAccount, setSelectedGoogleAccount] = useState<any>(null);
  const [adAccounts, setAdAccounts] = useState<any>([]);
  const [accessMessage, setAccessMessage] = useState("");
  const [accessGranted, setAccessGranted] = useState(false);

  const scopes =
    "https://www.googleapis.com/auth/youtube.readonly https://www.googleapis.com/auth/yt-analytics-monetary.readonly https://www.googleapis.com/auth/analytics.manage.users https://www.googleapis.com/auth/analytics.readonly https://www.googleapis.com/auth/adwords";

  const getData = async () => {
    try {
      setLoading(true);
      const { data } = await axios.post(
        `${ApiServerPath.API_SERVER_BASE_URL}/firebase/get_business_manager`,
        {
          adminId: connectId,
          platform: "GOOGLE",
          ...(workspaceId ? { workspaceId } : {}),
        }
      );
      setAdminDetails(data.results.business);
    } catch (e: any) {
      setErrorFromAdmin(e?.response?.data?.results?.error);
    } finally {
      setLoading(false);
    }
  };

  const provideAccess = async () => {
    try {
      setAccessLoading(true);
      setAccessMessage("");
      const { data } = await axios.post(
        "https://api.plai.io//googleads/link_google_mcc_account",
        {
          customerName: selectedGoogleAccount.name,
          customerId: selectedGoogleAccount.customerId,
          refreshToken: googleRefreshToken,
          adminId: connectId,
          managerId: selectedGoogleAccount.managerId,
        }
      );
      if (data.success) {
        setAccessGranted(true);
        setAccessMessage("Your account is now linked");
        setActiveStep(2);
      } else {
        setAccessMessage(
          data.results.error || "Something went wrong. Please contact support."
        );
      }
    } catch (e: any) {
      setAccessMessage(
        e.response?.data?.results?.error ||
        "Something went wrong. Please contact support."
      );
    } finally {
      setAccessLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const handleBack = () => {
    setActiveStep((prevActiveStep) => Math.max(prevActiveStep - 1, 0));
  };

  const onConnectGoogle = async (resp: any) => {
    setGoogleLoginLoading(true);
    const response = await getRefreshToken(resp.code);
    if (response?.refreshToken) {
      setGoogleRefreshToken(response?.refreshToken);
      const accounts = await getGoogleAdsAccounts(response?.refreshToken);
      setAdAccounts(accounts);
      setGoogleConnected(true);
      setSelectedGoogleAccount(null);
      setAccessGranted(false);
      setAccessMessage("");
      setActiveStep(1);
    }
    setGoogleLoginLoading(false);
  };

  const onSelectGoogleAccount = async (item: any) => {
    try {
      setAccountSelectLoading(true);
      if (!item?.isManager) {
        setSelectedGoogleAccount(item);
      } else {
        sweetAlert("You can not select manager account.");
      }
    } finally {
      setAccountSelectLoading(false);
    }
  };

  const stepSubtitle =
    activeStep === 0
      ? "Sign in with Google so we can find your ad accounts."
      : activeStep === 1
        ? "Choose the Google Ads account you want to share access to."
        : "You're all set — manager access has been granted.";

  const renderConnectGoogle = () => (
    <div className="rounded-xl border border-line bg-surface p-5 shadow-sm">
      <div className="mb-4 flex items-start gap-3.5">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[11px] border border-line bg-muted">
          <img
            src="images/global/google-icon.png"
            alt=""
            className="h-6 w-6 object-contain"
          />
        </div>
        <div>
          <p className="m-0 mb-1 text-[1.02rem] font-bold tracking-[-0.015em] text-ink">
            <AppText>Connect your Google Account</AppText>
          </p>
          <p className="m-0 text-[0.875rem] leading-relaxed text-ink-secondary">
            <AppText>
              Use the Google account that owns or manages the ads account you
              want to link.
            </AppText>
          </p>
        </div>
      </div>

      {googleConnected && (
        <div className="mb-3.5">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-success/20 bg-success-soft px-3 py-1.5 text-[0.76rem] font-semibold text-success">
            <FontAwesomeIcon icon={faCheckCircle as IconProp} />
            <AppText>Google connected</AppText>
          </span>
        </div>
      )}

      <GoogleOAuthProvider clientId={process.env.REACT_APP_CLIENT_ID || ""}>
        <GoogleScopeLogin
          scope={scopes}
          onSuccess={(resp: any) => onConnectGoogle(resp)}
          render={(renderProps: any) => (
            <button
              type="button"
              className="inline-flex h-[46px] w-full items-center justify-center rounded-[10px] bg-primary px-5 text-[0.95rem] font-semibold text-white shadow-btn transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-55"
              onClick={() => renderProps.onClick()}
              disabled={googleLoginLoading}
            >
              <AppText>
                {googleConnected ? "Reconnect Google" : "Connect Google"}
              </AppText>
            </button>
          )}
        />
      </GoogleOAuthProvider>
      <p className="mt-3 mb-0 text-[0.75rem] leading-snug text-ink-muted">
        <AppText>
          You stay in control — access can be reviewed or revoked anytime in
          Google Ads.
        </AppText>
      </p>
    </div>
  );

  const renderAdAccounts = () => {
    if (!googleConnected) {
      return (
        <div className="rounded-xl border border-line bg-surface p-5">
          <p className="m-0 mb-3 text-[0.875rem] text-ink-secondary">
            <AppText>Please connect your Google account first.</AppText>
          </p>
          <button
            type="button"
            className="inline-flex h-11 items-center justify-center rounded-[10px] border border-line-strong bg-transparent px-4 text-[0.9rem] font-semibold text-primary-text"
            onClick={() => setActiveStep(0)}
          >
            <AppText>Go to Connect Account</AppText>
          </button>
        </div>
      );
    }

    if (!adAccounts?.length) {
      return (
        <div className="rounded-[10px] border border-danger/30 bg-[#fef2f2] px-3.5 py-3 text-[0.875rem] leading-relaxed text-danger dark:bg-danger/10">
          <AppText>
            No Google Ads accounts were found for this login. Try reconnecting
            with a different Google account.
          </AppText>
        </div>
      );
    }

    return (
      <div className="rounded-xl border border-line bg-surface p-5 shadow-sm">
        <div className="mb-4 flex items-start gap-3.5">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[11px] border border-line bg-muted">
            <img
              src="images/global/google-icon.png"
              alt=""
              className="h-6 w-6 object-contain"
            />
          </div>
          <div>
            <p className="m-0 mb-1 text-[1.02rem] font-bold tracking-[-0.015em] text-ink">
              <AppText>Select ad account</AppText>
            </p>
            <p className="m-0 text-[0.875rem] leading-relaxed text-ink-secondary">
              <AppText>
                Pick a client ad account (not a manager account), then grant
                access.
              </AppText>
            </p>
          </div>
        </div>

        <div className="max-h-[260px] overflow-auto rounded-xl border border-line bg-surface sm:max-h-[280px]">
          <MaterialTreeView
            data={adAccounts}
            onSelect={(item: any) => onSelectGoogleAccount(item)}
            onChildSelect={(item: any) => onSelectGoogleAccount(item)}
          />
        </div>

        {accountSelectLoading ? (
          <p className="mt-2 mb-0 text-[0.8rem] text-ink-muted">
            <AppText>Loading...</AppText>
          </p>
        ) : (
          selectedGoogleAccount && (
            <div className="mt-3.5 rounded-xl border border-line bg-muted p-4">
              <p className="m-0 mb-1 text-[0.72rem] font-bold uppercase tracking-[0.05em] text-ink-muted">
                <AppText>Selected account</AppText>
              </p>
              <p className="m-0 mb-3.5 break-words text-base font-bold text-ink">
                {selectedGoogleAccount.name}
              </p>
              <AppButton
                title="Provide access"
                onClick={provideAccess}
                loading={accessLoading}
                className="google-lb-access-btn !h-[46px] !w-full !rounded-[10px] !bg-primary !px-5 !font-semibold !text-white !shadow-btn"
              />
              {accessMessage && !accessGranted && (
                <div className="mt-3 rounded-[10px] border border-danger/30 bg-[#fef2f2] px-3.5 py-3 text-[0.875rem] text-danger">
                  {accessMessage}
                </div>
              )}
            </div>
          )
        )}
      </div>
    );
  };

  const renderConnected = () => (
    <LinkBridgeSuccess
      platform="google"
      accountName={selectedGoogleAccount?.name}
      accountId={
        selectedGoogleAccount?.customerId
          ? String(selectedGoogleAccount.customerId)
          : undefined
      }
      managerName={adminDetails?.businessManagerName}
      managerId={
        adminDetails?.businessManagerId
          ? String(adminDetails.businessManagerId)
          : undefined
      }
      message={accessMessage}
    />
  );

  const renderRequestDetails = () => (
    <div className="mb-5 rounded-xl border border-primary-text/10 bg-primary-soft p-4">
      <p className="m-0 mb-3.5 text-[0.72rem] font-bold uppercase tracking-[0.06em] text-ink-muted">
        <AppText>Request Details</AppText>
      </p>
      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
        <div className="min-w-0">
          <span className="mb-1 block text-[0.72rem] font-semibold tracking-[0.02em] text-ink-muted">
            <AppText>Google Manager Account</AppText>
          </span>
          <span className="block break-words text-[0.92rem] font-semibold leading-snug text-ink">
            {adminDetails?.businessManagerName}
            {adminDetails?.businessManagerId
              ? ` (${adminDetails.businessManagerId})`
              : ""}
          </span>
        </div>
        <div className="min-w-0">
          <span className="mb-1 block text-[0.72rem] font-semibold tracking-[0.02em] text-ink-muted">
            <AppText>Access Level</AppText>
          </span>
          <span className="block text-[0.92rem] font-semibold leading-snug text-ink">
            <AppText>Manage</AppText>
          </span>
        </div>
      </div>
    </div>
  );

  const progressWidth = `${((activeStep + 1) / steps.length) * 100}%`;

  const renderStepper = () => (
    <>
      <div className="mb-5 rounded-xl border border-line bg-muted px-4 py-3.5">
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <div className="mt-3 h-1 overflow-hidden rounded-full bg-line" aria-hidden>
          <div
            className="h-full rounded-full bg-gradient-to-r from-primary to-[#5b8cff] transition-all duration-300"
            style={{ width: progressWidth }}
          />
        </div>
      </div>

      <div>
        {activeStep !== 2 && renderRequestDetails()}

        {googleLoginLoading ? (
          <div className="flex min-h-[220px] flex-col items-center justify-center gap-3 text-[0.9rem] text-ink-secondary">
            <Oval
              height={36}
              width={36}
              color="var(--color-primary)"
              visible
              ariaLabel="oval-loading"
              secondaryColor="var(--text-grey)"
              strokeWidth={2}
              strokeWidthSecondary={2}
            />
            <AppText>Connecting Google...</AppText>
          </div>
        ) : (
          <>
            {activeStep === 0 && renderConnectGoogle()}
            {activeStep === 1 && renderAdAccounts()}
            {activeStep === 2 && renderConnected()}
          </>
        )}
      </div>

      {activeStep !== 2 && (
        <div className="-mx-4 mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-line-muted bg-muted/70 px-4 py-3.5 sm:-mx-8 sm:px-8">
          <Button
            color="inherit"
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            <AppText>Back</AppText>
          </Button>
          <Box sx={{ flex: "1 1 auto" }} />
          {googleConnected && activeStep === 0 && (
            <Button onClick={() => setActiveStep(1)}>
              <AppText>Continue</AppText>
            </Button>
          )}
        </div>
      )}
    </>
  );

  if (loading) {
    return (
      <div className="box-border flex min-h-screen w-full max-w-full min-w-0 flex-col items-center overflow-x-hidden bg-[radial-gradient(1200px_420px_at_50%_-80px,rgba(15,96,253,0.07),transparent_60%),var(--bg-page)] px-3 pb-7 pt-16 text-ink sm:px-5 sm:pt-20">
        <div className="flex min-h-[280px] flex-col items-center justify-center gap-3 text-[0.9rem] text-ink-secondary">
          <Oval
            height={40}
            width={40}
            color="var(--color-primary)"
            visible
            ariaLabel="oval-loading"
            secondaryColor="var(--text-grey)"
            strokeWidth={2}
            strokeWidthSecondary={2}
          />
          <AppText>Loading request details...</AppText>
        </div>
      </div>
    );
  }

  return (
    <div className="box-border flex min-h-screen w-full max-w-full min-w-0 flex-col items-center overflow-x-hidden bg-[radial-gradient(1200px_420px_at_50%_-80px,rgba(15,96,253,0.07),transparent_60%),var(--bg-page)] px-3 pb-7 pt-16 text-ink sm:px-5 sm:pt-20">
      {adminDetails ? (
        <div className="flex w-full max-w-[680px] flex-col gap-5">
          <div className="lb-card w-full overflow-visible rounded-card border border-line bg-surface p-0 shadow-card">
            <div className="px-4 pb-2 pt-6 sm:px-8 sm:pt-7">
              <div className="mb-5 flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2.5">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-[10px] border border-line bg-surface shadow-sm">
                    <img
                      src="images/global/google-icon.png"
                      alt=""
                      className="h-[22px] w-[22px] object-contain"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="m-0 text-[0.92rem] font-bold leading-tight text-ink">
                      <AppText>Google Link Bridge</AppText>
                    </p>
                    <p className="m-0 hidden text-[0.75rem] text-ink-muted sm:block">
                      <AppText>Secure manager access request</AppText>
                    </p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-line bg-muted px-2.5 py-1.5 text-[0.72rem] font-bold tracking-[0.02em] text-ink-secondary">
                  <AppText>Step</AppText>{" "}
                  <strong className="text-primary-text">
                    {activeStep + 1}/{steps.length}
                  </strong>
                </span>
              </div>

              <div className="mb-5">
                <p className="m-0 mb-2 text-[1.22rem] font-bold leading-snug tracking-[-0.03em] text-ink sm:text-[1.45rem]">
                  <AppText
                    values={{
                      managerName: adminDetails?.businessManagerName,
                    }}
                  >
                    {`{managerName} is requesting manager access to your Google Ads account`}
                  </AppText>
                </p>
                <p className="m-0 max-w-[540px] text-[0.875rem] leading-relaxed text-ink-secondary sm:text-[0.925rem]">
                  <AppText>{stepSubtitle}</AppText>
                </p>
              </div>
              {renderStepper()}
            </div>
          </div>
        </div>
      ) : (
        <div className="max-w-[420px] rounded-card border border-line bg-surface px-6 py-12 text-center shadow-card">
          <FontAwesomeIcon
            className="mb-3 text-[42px] text-danger"
            icon={faTriangleExclamation as IconProp}
          />
          <p className="m-0 text-[0.95rem] leading-relaxed text-ink-secondary">
            {errorFromAdmin}
          </p>
        </div>
      )}
    </div>
  );
};

export default GoogleLinkBridge;
